import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { inviteService, type InviteValidateResponse } from '@/services/inviteService';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/lib/api';

const acceptSchema = z.object({
  name: z.string().min(2, { message: 'invite.name_min' }),
  password: z.string().min(8, { message: 'invite.password_min' }),
});

type AcceptFormValues = z.infer<typeof acceptSchema>;

type ErrorCode = 'invite_expired' | 'invite_consumed' | 'invite_invalid';

function errorTitleKey(code: ErrorCode): string {
  if (code === 'invite_expired') return 'invite.error_expired_title';
  if (code === 'invite_consumed') return 'invite.error_consumed_title';
  return 'invite.error_invalid_title';
}

function errorBodyKey(code: ErrorCode): string {
  if (code === 'invite_expired') return 'invite.error_expired_body';
  if (code === 'invite_consumed') return 'invite.error_consumed_body';
  return 'invite.error_invalid_body';
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();

  const token = searchParams.get('token') || '';

  const [validateResult, setValidateResult] = useState<InviteValidateResponse | null>(null);
  const [validating, setValidating] = useState(true);
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const didValidate = useRef(false);

  const form = useForm<AcceptFormValues>({
    resolver: zodResolver(acceptSchema),
    defaultValues: { name: '', password: '' },
  });

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    // Guard against React 18 StrictMode double-effect (mirrors MetaCallbackPage)
    if (didValidate.current) return;
    didValidate.current = true;

    void (async () => {
      try {
        const result = await inviteService.validate(token);
        setValidateResult(result);
      } catch (err) {
        if (err instanceof ApiError) {
          const code = err.code as ErrorCode | undefined;
          setErrorCode(
            code === 'invite_expired' || code === 'invite_consumed' || code === 'invite_invalid'
              ? code
              : 'invite_invalid'
          );
        } else {
          setErrorCode('invite_invalid');
        }
      } finally {
        setValidating(false);
      }
    })();
  }, [token, navigate]);

  async function onSubmit(values: AcceptFormValues) {
    setSubmitError(null);
    try {
      const result = await inviteService.accept(token, {
        name: values.name,
        password: values.password,
      });
      localStorage.setItem('jwt', result.access_token);
      await refreshUser();
      setSuccess(true);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const code = err.code as ErrorCode | undefined;
        if (code === 'invite_expired' || code === 'invite_consumed' || code === 'invite_invalid') {
          setErrorCode(code);
        } else {
          setSubmitError(t('common.error_generic'));
        }
      } else {
        setSubmitError(t('common.error_generic'));
      }
    }
  }

  // No token — redirect handled in useEffect; render nothing interim
  if (!token) return null;

  if (validating) {
    return (
      <AuthShell title={t('invite.loading')}>
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </AuthShell>
    );
  }

  if (errorCode) {
    return (
      <AuthShell title={t(errorTitleKey(errorCode))}>
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">{t(errorBodyKey(errorCode))}</p>
          <Button asChild className="w-full h-11">
            <Link to="/login">{t('invite.login_cta')}</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell title={t('invite.success')}>
        <p className="text-center text-sm text-muted-foreground">{t('common.loading')}</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('invite.title')}
      subtitle={
        validateResult
          ? t('invite.tenant_role_line', {
              tenant_name: validateResult.tenant_name,
              role: validateResult.role === 'admin' ? t('team.role_admin') : t('team.role_member'),
            })
          : undefined
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('invite.name_label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('invite.name_placeholder')}
                    autoComplete="name"
                    autoFocus
                    className="h-11"
                    data-testid="accept-name-input"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('invite.password_label')}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t('invite.password_placeholder')}
                    autoComplete="new-password"
                    className="h-11"
                    data-testid="accept-password-input"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {submitError && (
            <div role="alert" className="text-sm text-destructive">
              {submitError}
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-11"
            disabled={form.formState.isSubmitting}
            data-testid="accept-submit"
          >
            {form.formState.isSubmitting ? t('common.loading') : t('invite.submit')}
          </Button>

          <p className="text-center text-sm">
            <Link
              to="/login"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('invite.login_cta')}
            </Link>
          </p>
        </form>
      </Form>
    </AuthShell>
  );
}
